import {Auth, Hub} from 'aws-amplify';
import ServerData from '../serverData';
import CacheData from './cacheData';
import {BllinkLogger} from './bllink_loggers';
import {isMobileView} from './isMobileView';

/**
 * where a manager lands right after logging in.
 *
 * the decision is made at the login itself and nowhere else, so no page has to undo a landing it
 * has already started rendering.
 * */

/**
 * the mobile manager app is served from /m on the same origin, so the cognito session carries over
 * and no second login is needed
 * */
const MOBILE_APP_URL = '/m/';

const SOCIAL_LOGIN_PENDING_KEY = 'socialLoginPending';

/**
 * TEMPORARY - the mobile app is opening up partner by partner and will end up open to every manager.
 * when it does, drop this list together with getManagerProfile below: the whole decision collapses
 * into `isMobileView()`, and nothing else here has to change.
 *
 * these are partner externalIDs, because the profile api never sends the numeric partner id - it
 * returns the partner's externalID. partner 47 (IndianMagic.privatePartnerId, the entity all
 * individual managers hang off) is 'private'. case does not matter, write them however the admin
 * screens show them.
 * */
const MOBILE_APP_PARTNER_EXTERNAL_IDS = ['private'];

/**
 * @param {{partnerID?: string, isPrivateBuildingPartner?: boolean}} profile
 * */
function shouldUseMobileApp(profile) {
    if (!isMobileView()) {
        return false;
    }
    // partner 47 is the one partner the api also reports with a dedicated flag, which it computes as
    // `partner.partnerID === 47`. we read the flag to name the partner, not to let it past the list,
    // so taking 'private' off the list is all it takes to drop them again
    const partnerID = profile?.isPrivateBuildingPartner === true
        ? 'private'
        : (profile?.partnerID || '').trim().toLowerCase();
    return MOBILE_APP_PARTNER_EXTERNAL_IDS.some(id => id.trim().toLowerCase() === partnerID);
}

/**
 * the manager's partner is only known from the profile, so we ask for it here, with the token the
 * login just issued, instead of waiting for the manager page to ask for it later.
 *
 * the answer goes into the same cache key getUserProfile reads (5 hours), so that later fetch is
 * served from cache and never reaches the server. we always go to the server here rather than
 * through getOrCache, because the cached profile may still belong to the manager who logged out.
 *
 * @param {string} accessToken
 * */
async function getManagerProfile(accessToken) {
    const client = new ServerData(null, null, {accessToken: accessToken});
    const profile = await client.managers.getProfile();
    CacheData.cache('profile', profile);
    return profile;
}

/**
 * the single "where does this manager go now that they are logged in" decision, including the
 * redirect itself.
 *
 * @param {string} accessToken token issued by the login that just succeeded
 * @param {string|null} defaultUrl where this login would have gone on its own. null means the
 *        manager is already on that page (social login comes back to it), so only a handoff to the
 *        mobile app is worth a navigation.
 * @return {Promise<boolean>} true when the manager was sent to the mobile app
 * */
export async function goToPostLoginPage(accessToken, defaultUrl) {
    let url = defaultUrl;
    let usedMobileApp = false;

    try {
        const profile = await getManagerProfile(accessToken);
        if (shouldUseMobileApp(profile)) {
            url = MOBILE_APP_URL;
            usedMobileApp = true;
        }
    } catch (e) {
        // the landing page fetches the profile itself, so failing here costs the mobile app handoff
        // and nothing more - the manager still lands where this login was going anyway
        BllinkLogger.error('could not decide the post login landing page', e);
    }

    if (url) {
        window.location.href = url;
    }
    return usedMobileApp;
}

/**
 * social login hands the browser to cognito, so there is no moment to decide anything before
 * leaving. this flag is what lets us recognise the way back.
 * */
export function markSocialLoginPending() {
    window.localStorage.setItem(SOCIAL_LOGIN_PENDING_KEY, 'true');
}

async function handleSocialLoginReturn() {
    if (window.localStorage.getItem(SOCIAL_LOGIN_PENDING_KEY) !== 'true') {
        return;
    }
    // taken before the await so the boot call and the amplify event below cannot both act on it
    window.localStorage.removeItem(SOCIAL_LOGIN_PENDING_KEY);

    let accessToken;
    try {
        const user = await Auth.currentAuthenticatedUser();
        accessToken = user.signInUserSession.accessToken.jwtToken;
    } catch (e) {
        // amplify has not finished with the cognito redirect yet, or the manager walked away from
        // the cognito page and came back on their own. put the flag back: if it was the first, the
        // amplify event below runs this again, and if it was the second nothing else will.
        markSocialLoginPending();
        return;
    }

    // cognito sends the manager to the all buildings page, which is where they already are, so
    // there is no default url to navigate to here
    await goToPostLoginPage(accessToken, null);
}

/**
 * call once when the app boots. does nothing unless the manager is coming back from a social login.
 * @return {function} unsubscribe
 * */
export function watchForSocialLoginReturn() {
    // registered first so it catches a login that completes while the call below is still waiting
    const stopListening = Hub.listen('auth', ({payload}) => {
        if (payload?.event === 'signIn' || payload?.event === 'cognitoHostedUI') {
            handleSocialLoginReturn();
        }
    });
    handleSocialLoginReturn();
    return stopListening;
}
